import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PrimaryButton } from "../../components/ui";
import { BarChart } from "../../components/charts";
import { CORPUS_SIZE } from "../../data/corpus";

const COPY = {
  1: {
    label: "HOW IT WORKS · 01",
    title: <>你只需要輸入<br />考試日期</>,
    desc: "程式把詞數、複習輪次和剩餘天數一起算，得出每天的配額。它會隨你的正確率每天調整。",
  },
  2: {
    label: "HOW IT WORKS · 02",
    title: <>選擇題認得出，<br />配對題才算記牢</>,
    desc: "新詞先用選擇題建立印象；同一個詞第三次出現時換成配對題，要在四個相近選項中選對，才會判定為掌握。",
  },
  3: {
    label: "HOW IT WORKS · 03",
    title: <>答錯的詞<br />自己會回來找你</>,
    desc: "每答對一次，下次出現的間隔就拉長；答錯就退回 10 分鐘。你不需要記得哪些詞要複習。",
  },
} as const;

export default function HowItWorks() {
  const { step: stepParam } = useParams();
  const navigate = useNavigate();
  const step = Number(stepParam);
  if (![1, 2, 3].includes(step)) return <Navigate to="/onboarding/how/1" replace />;
  const copy = COPY[step as 1 | 2 | 3];

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col pt-[clamp(40px,7vh,64px)] px-[clamp(18px,6vw,28px)] pb-[clamp(20px,4vh,36px)]">
      <div className="flex items-center justify-between">
        <div className="flex gap-[6px] items-center">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`h-[5px] rounded-[3px] ${i === step ? "w-[18px] bg-ink" : "w-[5px] bg-sand"}`} />
          ))}
        </div>
        <Link to="/level-test/intro" className="text-[13.5px] text-ink3 no-underline">跳過</Link>
      </div>

      <div className="mt-[clamp(28px,5vh,50px)]">
        <div className="font-grotesk text-[11px] font-semibold tracking-[.16em] text-ink4">{copy.label}</div>
        <h2 className="mt-[14px] mb-0 text-[clamp(24px,6vw,30px)] leading-[1.32] font-bold text-ink">{copy.title}</h2>
        <p className="mt-[14px] mb-0 text-[15px] leading-[1.75] text-ink2 [text-wrap:pretty]">{copy.desc}</p>
      </div>

      {step === 1 && <Step1 />}
      {step === 2 && <Step2 />}
      {step === 3 && <Step3 />}

      <div className={`mt-auto ${step === 1 ? "pt-[clamp(20px,3vh,30px)]" : "pt-[clamp(18px,3vh,28px)]"}`}>
        <PrimaryButton
          onClick={() => navigate(step === 3 ? "/level-test/intro" : `/onboarding/how/${step + 1}`)}
        >
          {step === 3 ? "明白了，開始測試" : "下一頁"}
        </PrimaryButton>
      </div>
    </div>
  );
}

function Step1() {
  const rows = [
    { label: "詞單詞數", value: CORPUS_SIZE.toLocaleString("en-US") },
    { label: "DSE 英文卷二", value: "8 Apr 2027" },
    { label: "剩餘天數", value: "247" },
  ];
  return (
    <>
      <div className="mt-[clamp(22px,4vh,36px)] p-[clamp(14px,4vw,20px)] rounded-[18px] bg-white border border-line">
        {rows.map((r, i) => (
          <div key={r.label} className={`flex items-center justify-between ${
            i === 0 ? "pb-[13px]" : `pt-[13px] border-t border-line-soft ${i === rows.length - 1 ? "" : "pb-[13px]"}`
          }`}>
            <span className="text-[13.5px] text-ink2">{r.label}</span>
            <span className="font-grotesk text-[15px] font-semibold text-ink">{r.value}</span>
          </div>
        ))}
      </div>

      <div className="flex justify-center py-[clamp(10px,2vh,16px)]">
        <span className="text-[15px] text-ink4">↓</span>
      </div>

      <div className="hero-card p-[clamp(16px,4.5vw,22px)] rounded-[18px]">
        <div className="text-[12.5px] text-cream/60">你的每日配額</div>
        <div className="flex items-baseline gap-2 mt-[6px]">
          <span className="font-grotesk text-[clamp(36px,9vw,44px)] font-semibold text-lime leading-none tracking-[-.03em]">14</span>
          <span className="text-[15px] text-cream/75">個新詞 / 天</span>
        </div>
        <div className="text-[13px] leading-[1.6] text-cream/60 mt-[10px]">＋ 每天約 40 個舊詞複習。整體約 6 分鐘。</div>
      </div>
    </>
  );
}

function Step2() {
  return (
    <>
      <div className="mt-[clamp(20px,3.5vh,32px)] p-[clamp(12px,3.5vw,17px)] rounded-2xl bg-white border border-line">
        <div className="flex items-center justify-between">
          <span className="font-grotesk text-[12px] font-semibold tracking-[.1em] text-ink4">選擇題 MC</span>
          <span className="text-[12px] text-ink3">第 1–2 次</span>
        </div>
        <div className="font-grotesk text-[22px] font-semibold text-ink mt-[13px] mb-[12px]">candid</div>
        <div className="flex flex-col gap-[6px]">
          <div className="px-[12px] py-[10px] rounded-[10px] bg-green-bg border border-green text-[14px] text-green-text">坦率的、直言的</div>
          <div className="px-[12px] py-[10px] rounded-[10px] bg-cream border border-line-soft text-[14px] text-ink4">謹慎的、含蓄的</div>
        </div>
      </div>

      <div className="mt-[12px] p-[clamp(12px,3.5vw,17px)] rounded-2xl bg-white border border-line">
        <div className="flex items-center justify-between">
          <span className="font-grotesk text-[12px] font-semibold tracking-[.1em] text-ink4">配對題 MATCHING</span>
          <span className="text-[12px] text-accent">第 3 次起</span>
        </div>
        <div className="flex gap-2 mt-[13px]">
          <div className="flex-1 flex flex-col gap-[6px]">
            <div className="p-[10px] rounded-[10px] bg-ink font-grotesk text-[13.5px] font-semibold text-cream">candid</div>
            <div className="p-[10px] rounded-[10px] bg-cream border border-line-soft font-grotesk text-[13.5px] font-semibold text-ink">discreet</div>
          </div>
          <div className="flex-1 flex flex-col gap-[6px]">
            <div className="p-[10px] rounded-[10px] bg-cream border border-line-soft text-[13px] text-ink">謹慎的</div>
            <div className="p-[10px] rounded-[10px] bg-cream border border-line-soft text-[13px] text-ink">坦率的</div>
          </div>
        </div>
      </div>
    </>
  );
}

function Step3() {
  return (
    <>
      <div className="mt-[clamp(24px,4vh,38px)]">
        <BarChart
          exact
          height={128}
          labelClass="font-grotesk text-[11.5px] text-ink3"
          bars={[
            { label: "10分", h: 22, color: "var(--color-blush)" },
            { label: "1天", h: 42, color: "var(--color-sand)" },
            { label: "4天", h: 62, color: "var(--color-sand)" },
            { label: "12天", h: 84, color: "var(--color-green)" },
            { label: "30天", h: 106, color: "var(--color-ink)" },
          ]}
        />
        <div className="text-[13px] leading-[1.6] text-ink2 mt-[16px] px-[2px]">同一個詞的複習間隔。到考期前，每個詞至少會出現 5 次。</div>
      </div>

      <div className="mt-[clamp(18px,3vh,28px)] px-[clamp(13px,3.5vw,17px)] py-[16px] rounded-2xl bg-paper border border-line-tab">
        <div className="text-[13.5px] leading-[1.65] text-ink2">
          反覆答錯 4 次以上的詞會被標成<span className="text-accent font-bold">「易忘」</span>，改用配對題和例句填空處理。
        </div>
      </div>
    </>
  );
}
